import type { ClientAction, PlayerView } from '@sh/shared'
import { ConfirmHint, HoldToConfirm } from '../../components/Confirm.js'
import { ChancellorIcon, PresidentIcon } from '../../components/Icons.js'
import { usePendingCard } from '../../components/Nudge.js'
import { WaitingOn } from '../../components/Waiting.js'

export function NominationPhase({
  view,
  act,
}: {
  view: PlayerView
  act: (a: ClientAction) => void
}) {
  const you = view.you
  const isPresident = you?.id === view.presidentId
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  // Framed and named, but never buzzed or shaken at. This is the one moment
  // the game is explicitly for talking - nine people are lobbying the
  // President out loud - and a phone twitching through it would be arguing
  // back. Saying what is outstanding costs nobody their turn to talk.
  const pending = usePendingCard(true, false)

  if (!isPresident) {
    return (
      <section className="card">
        <h2 className="card__title">Election</h2>
        <WaitingOn who={name(view.presidentId ?? '')}>
          <strong>{name(view.presidentId ?? '')}</strong> is President and is choosing a Chancellor
        </WaitingOn>
        <p className="card__hint">Talk it through. Nothing happens until they nominate.</p>
      </section>
    )
  }

  return (
    <section ref={pending.ref} className={`card ${pending.className}`}>
      <h2 className="card__title">You are President</h2>
      <p className="card__hint">
        Nominate a Chancellor. Term-limited players are not listed. The name you hold goes
        straight to the ballot — there is no taking it back.
      </p>
      <div className="choices">
        {view.legalTargets.map((id) => (
          <HoldToConfirm
            key={id}
            className="btn--choice btn--choice-hold"
            onConfirm={() => act({ type: 'nominate', nomineeId: id })}
            testId={`nominate-${name(id)}`}
          >
            Nominate {name(id)}
          </HoldToConfirm>
        ))}
      </div>
      <p className="card__hint"><ConfirmHint subject="name" /></p>
      {view.legalTargets.length === 0 && (
        <p className="error">No eligible Chancellor. Tell the host — this should not happen.</p>
      )}
    </section>
  )
}

/**
 * Who is being voted on, as two placards rather than a run-on line. At a table
 * of ten the question is always "is it *these two*", and people were reading
 * "Ana & Beto" without registering which of them was asking for the
 * Chancellery and which was already holding office.
 */
function Government({ view }: { view: PlayerView }) {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  const you = view.you?.id
  return (
    <div className="gov">
      <div className={`gov__card gov__card--pres ${you === view.presidentId ? 'is-you' : ''}`}>
        <span className="gov__icon" aria-hidden="true">
          <PresidentIcon />
        </span>
        <span className="gov__role">President</span>
        <span className="gov__name">{name(view.presidentId ?? '')}</span>
        <span className="gov__note">already in office</span>
      </div>
      <span className="gov__join" aria-hidden="true">
        +
      </span>
      <div className={`gov__card gov__card--chan ${you === view.nomineeId ? 'is-you' : ''}`}>
        <span className="gov__icon" aria-hidden="true">
          <ChancellorIcon />
        </span>
        <span className="gov__role">Chancellor</span>
        <span className="gov__name">{name(view.nomineeId ?? '')}</span>
        <span className="gov__note">nominated — up for this vote</span>
      </div>
    </div>
  )
}

export function VotingPhase({
  view,
  act,
}: {
  view: PlayerView
  act: (a: ClientAction) => void
}) {
  const you = view.you
  const cast = you ? view.ballotsCast.includes(you.id) : false
  const alive = view.players.filter((p) => p.alive).length
  const outstanding = view.players
    .filter((p) => p.alive && !view.ballotsCast.includes(p.id))
    .map((p) => p.name)
  // The ballot is open, which means the talking is over and every other phone
  // at the table is waiting on this one.
  const pending = usePendingCard(!cast)

  return (
    <section ref={pending.ref} className={`card ${pending.className}`}>
      <h2 className="card__title">This government?</h2>
      <Government view={view} />

      {!you?.alive ? (
        <p className="waiting">You have been executed. You do not vote.</p>
      ) : cast ? (
        <>
          <WaitingOn testId="ballot-cast">
            Ballot cast — {view.ballotsCast.length} of {alive} in. They all open together
          </WaitingOn>
          {/*
            Nothing here re-reads the ballot, and the server stops sending it
            the moment it is cast. Re-checking your own vote sounds harmless -
            it is your secret - but the hold happens at the table, in front of
            the people the secret is kept from, and a shoulder reads a held
            card as easily as its owner does. A "change your vote" control has
            the same problem twice over: it has to name the vote it would
            change. So the ballot leaves the phone the instant it is cast.
          */}
          <p className="card__hint">
            Your ballot is in. It cannot be changed, and it is no longer on this phone to check.
          </p>
        </>
      ) : (
        <>
          <p className="card__hint">
            <ConfirmHint subject="ballot" oneClick /> It is final, and nobody sees it until the
            last one lands.
          </p>
          <div className="choices choices--row choices--ballot">
            <HoldToConfirm
              className="btn--ja"
              oneClick
              onConfirm={() => act({ type: 'cast_ballot', ja: true })}
              testId="vote-ja"
            >
              Ja!
            </HoldToConfirm>
            <HoldToConfirm
              className="btn--nein"
              oneClick
              onConfirm={() => act({ type: 'cast_ballot', ja: false })}
              testId="vote-nein"
            >
              Nein
            </HoldToConfirm>
          </div>
        </>
      )}

      <p className="waiting">
        {outstanding.length > 0 ? `Still to vote: ${outstanding.join(', ')}` : 'Every ballot is in.'}
      </p>
    </section>
  )
}
